import { useEffect, useRef, useState } from "react";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { useAppContext } from "../../../../AppContext";
import { UserRole } from "../../../../common/enums";
import { getCurrentUserRole } from "../../../../common/infrastructure/helpers/userHelper";
import { ISupportChatDetail, ISupportMessage } from "../../models/supportChatModels";

const GROUPING_THRESHOLD_MS = 2 * 60 * 1000;

export const useSupportChat = (id: string | undefined) => {
  const { currentUser } = useAppContext();
  const isGlobalAdmin = getCurrentUserRole(currentUser) === UserRole.GlobalAdmin;
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const pendingTempIdRef = useRef<number | null>(null);

  const [optimisticMessages, setOptimisticMessages] = useState<ISupportMessage[]>([]);

  const { data: chat, isLoading, refetch } = useApiQuery<ISupportChatDetail>(
    {
      queryKey: [endpointsMetadata.supportChat.getChat, id],
      endpoint: `${endpointsMetadata.supportChat.getChat}/${id}`,
      globalIsLoading: false,
      enabled: !!id,
    },
    { refetchInterval: 5000 },
  );

  // Scroll to bottom on new optimistic message (scrollTop=0 in column-reverse)
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container && optimisticMessages.length > 0) {
      container.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [optimisticMessages.length]);


  const { mutate: sendMessageMutation, isLoading: isSending } = useApiMutation({
    onSuccess: () => {
      pendingTempIdRef.current = null;
      refetch();
    },
    onError: () => {
      const tempId = pendingTempIdRef.current;
      pendingTempIdRef.current = null;
      setOptimisticMessages((prev) => prev.filter((m) => m.id !== tempId));
    },
  });

  const handleSend = (content: string, attachmentIds: number[]) => {
    if (!chat || !currentUser) return;

    const tempId = -Date.now();
    pendingTempIdRef.current = tempId;

    const optimisticMsg: ISupportMessage = {
      id: tempId,
      senderId: currentUser.id,
      senderName: `${currentUser.firstName ?? ""} ${currentUser.lastName ?? ""}`.trim() || "Вие",
      senderProfilePhotoUrl: currentUser.profilePhotoUrl ?? null,
      content,
      sentAt: new Date().toISOString(),
      attachments: [],
    };

    setOptimisticMessages((prev) => [...prev, optimisticMsg]);

    sendMessageMutation({
      endpoint: endpointsMetadata.supportChat.sendMessage,
      method: "post",
      payload: { chatId: chat.id, content, attachmentFileIds: attachmentIds },
      invalidateKeys: [[endpointsMetadata.supportChat.getAll]],
      disableSuccessToast: true,
      errorMessage: "Съобщението не беше изпратено. Моля, опитайте отново.",
    });
  };

  const { mutate: closeChat, isLoading: isClosing } = useApiMutation({
    onSuccess: () => refetch(),
  });

  const { mutate: reopenChat, isLoading: isReopening } = useApiMutation({
    onSuccess: () => refetch(),
  });

  const handleCloseChat = () => {
    closeChat({
      endpoint: `${endpointsMetadata.supportChat.closeChat}/${id}`,
      method: "put",
      disableSuccessToast: false,
      successMessage: "Чатът беше затворен.",
    });
  };

  const handleReopenChat = () => {
    reopenChat({
      endpoint: `${endpointsMetadata.supportChat.reopenChat}/${id}`,
      method: "put",
      disableSuccessToast: false,
      successMessage: "Чатът беше отворен отново.",
    });
  };

  const pendingOptimistic = optimisticMessages.filter(
    (om) => !chat?.messages.some(
      (rm) => rm.senderId === om.senderId && rm.content === om.content,
    ),
  );
  const allMessages = [...(chat?.messages ?? []), ...pendingOptimistic];

  const groupedMessages = allMessages.map((msg, index) => {
    const prev = index > 0 ? allMessages[index - 1] : null;
    const next = index < allMessages.length - 1 ? allMessages[index + 1] : null;

    const isGroupedWithPrev =
      prev != null &&
      prev.senderId === msg.senderId &&
      new Date(msg.sentAt).getTime() - new Date(prev.sentAt).getTime() < GROUPING_THRESHOLD_MS;

    const isGroupedWithNext =
      next != null &&
      next.senderId === msg.senderId &&
      new Date(next.sentAt).getTime() - new Date(msg.sentAt).getTime() < GROUPING_THRESHOLD_MS;

    return { msg, isGroupedWithPrev, isGroupedWithNext };
  });

  return {
    chat,
    isLoading,
    groupedMessages,
    handleSend,
    handleCloseChat,
    handleReopenChat,
    isSending,
    isClosing,
    isReopening,
    messagesContainerRef,
    isGlobalAdmin,
    currentUser,
  };
};
